import React, { useState, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  TextField,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { Accordion } from '../atoms';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import LoadMoreButton from '../atoms/LoadMore';
import { ScrollableBox, EditableTextField } from '../atoms';
import useFullScreen from '../../hooks/useFullScreen';
import ConfirmationDialog from '../common/ConfirmationDialog';
import SectionHeader from '../atoms/Header';
import TopBarButtons from '../atoms/TopBarButtons';
import EmptyContentPlaceholder from '../atoms/EmptyContentPlaceholder';
import MarkdownRenderer from '../common/MarkdownRenderer';
import SectionDetailsRenderer from '../common/SectionDetailsRenderer';
// Import the common hooks
import useSectionCRUD from '../../hooks/useSectionCRUD';
import useContentActions from '../../hooks/useContentActions';
import useNotification from '../../hooks/useNotification';
import LoadingBox from '../atoms/LoadingBox';
import { capitalizeFirst } from '../../utils/textTransformations';
import useExplain from '../../hooks/useExplain';
import ExplanationModal from '../atoms/ExplanationModal';
import useImprove from '../../hooks/useImprove';
import ImprovementModal from '../atoms/ImprovementModal';
import conversationService from '../../services/conversationService';
import useUserRoleVersions from '../../hooks/useUserRoleVersions';
import VersionSelector from '../atoms/VersionSelector';
import AddSectionModal from '../atoms/AddSectionModal';
import InlineImproveInput from '../common/InlineImproveInput';
import useParsedSections from '../../hooks/useParsedSections';

/**
 * A split-view layout component that shows conversation on the left
 * and parsed user roles in accordion format on the right
 */
const SplitViewUserRoles = ({ conversation, children, isStreaming, streamingContent, projectId, onConversationUpdate }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const { showNotification } = useNotification();
  const rolesPanelRef = useRef(null);

  // Helper function to format role titles to sentence case
  const formatRoleTitleToSentenceCase = (title) => {
    if (!title) return title;

    // Check if title starts with a number and period (e.g., "1. System Administrator")
    const numberedMatch = title.match(/^(\d+\.\s*)(.+)$/);
    if (numberedMatch) {
      // Keep the number prefix, capitalize only the first letter of the actual title
      const numberPrefix = numberedMatch[1];
      const actualTitle = numberedMatch[2];
      return numberPrefix + capitalizeFirst(actualTitle.toLowerCase());
    }

    // For non-numbered titles, capitalize first letter of the whole string
    return capitalizeFirst(title.toLowerCase());
  };

  // Track if we're doing a local update (passed to CRUD hooks)
  const isLocalUpdateRef = useRef(false);

  // Use version management for user roles
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = useUserRoleVersions(conversation, isStreaming, streamingContent, isLocalUpdateRef);

  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedRoles,
    setSections: setParsedRoles,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef: rolesContentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  // State for managing expanded/visible roles
  const [expandedRoles, setExpandedRoles] = useState({});
  // Section highlight from search navigation (?section=sectionId)
  const useSectionHighlight = require('../../hooks/useSectionHighlight').default;
  const { highlightRef, shouldHighlight } = useSectionHighlight(expandedRoles, setExpandedRoles);

  const [visibleRoles, setVisibleRoles] = useState(10);

  // Track previous version index to detect actual version changes
  const prevVersionIndexRef = useRef(currentVersionIndex);

  // Version switching: reset visible roles when switching versions
  useEffect(() => {
    if (currentVersion) {
      // Only reset visible roles when actually switching versions
      if (prevVersionIndexRef.current !== currentVersionIndex) {
        setVisibleRoles(10);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);

  // Check if this is a user roles conversation
  const isUserRolesConversation = conversation?.taskId === 'user-roles' ||
    (conversation?.messages?.some(msg =>
      msg.role === 'assistant' &&
      (msg.content?.includes('User Roles') ||
        msg.content?.includes('**Description:**') ||
        msg.content?.includes('Primary Responsibilities:'))
    ));

  const showRolePanel = parsedRoles.length > 0;

  const handleRoleToggle = (roleId) => {
    setExpandedRoles(prev => ({
      ...prev,
      [roleId]: !prev[roleId]
    }));
  };

  const handleLoadMore = () => {
    setVisibleRoles(prev => Math.min(prev + 10, parsedRoles.length));
  };

  const handleShowAll = () => {
    setVisibleRoles(parsedRoles.length);
  };

  // Use CRUD operations hook
  const {
    editingSection: editingRole,
    editedContent,
    editedTitle,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setEditedTitle,
    setDeleteConfirmation,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteSection: handleDeleteRoleClick,
    handleDeleteSectionConfirmed: handleDeleteConfirmGeneric,
    handleCopySection: handleCopyRoleOriginal
  } = useSectionCRUD(conversation, parsedRoles, setParsedRoles, setExpandedRoles, rolesContentRef, updateCurrentVersion, currentVersion, isLocalUpdateRef, onConversationUpdate,
    markLocalEdit
  );

  // Custom delete handler that properly reconstructs user roles markdown format
  const handleDeleteConfirm = async () => {
    const role = deleteConfirmation.section;
    setDeleteConfirmation({ open: false, section: null });

    if (!role) return;

    try {
      // Remove the role from the roles array
      const updatedRoles = parsedRoles.filter(r => r.id !== role.id);

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedRoles(updatedRoles);

      // Remove expanded state for this role
      setExpandedRoles(prev => {
        const updated = { ...prev };
        delete updated[role.id];
        return updated;
      });

      // Reconstruct the full content with proper user roles format
      let updatedContent = '';
      updatedRoles.forEach((r, idx) => {
        if (idx > 0) updatedContent += '\n\n';
        updatedContent += `### ${r.title}\n\n${r.content}`;
      });

      // Update content ref
      rolesContentRef.current = updatedContent.trim();

      // Update the current version with new roles
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          roles: updatedRoles,
          parsedContent: updatedRoles
        });
      }

      // Save to backend
      const updatedMessages = [...conversation.messages];
      let messageUpdated = false;

      // Try to match by messageIndex first (most reliable for V1)
      if (currentVersion && currentVersion.messageIndex !== undefined) {
        const idx = currentVersion.messageIndex;
        if (updatedMessages[idx] && updatedMessages[idx].role === 'assistant') {
          updatedMessages[idx] = {
            ...updatedMessages[idx],
            content: updatedContent.trim()
          };
          messageUpdated = true;
          console.log('✅ UserRoles Delete: Updated message by messageIndex at index', idx);
        }
      }

      // Fallback: update the last assistant message if we couldn't find by messageIndex
      if (!messageUpdated) {
        console.warn('⚠️ UserRoles Delete: No messageIndex match, updating last assistant message');
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Update LHS immediately by calling onConversationUpdate with updated messages
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id, updatedMessages);
        console.log('✅ UserRoles Delete: Called onConversationUpdate');
      }

      showNotification({
        message: `"${role.title}" deleted successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting role:', error);
      showNotification({
        message: `Failed to delete "${role.title}"`,
        type: 'error'
      });
    }
  };

  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainSection,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Use improve hook
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    registerSectionRef,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Use content actions hook
  const {
    isExporting,
    isSaving: isSavingDocument,
    handleExport: handleExportRoles,
    handleCopy: handleCopyRoles,
    handleRefresh: handleRefreshRoles,
    handleSaveToDocument,
    getExportContent,
  } = useContentActions(rolesContentRef, 'User Roles', projectId, conversation?._id);

  // Full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // Initialize validation state for roles with version-specific IDs
  const initialValidationState = parsedRoles.reduce((acc, role) => {
    // Create version-specific validation ID
    const versionSpecificId = `${role.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});

  // Use the validation hook with version-specific IDs
  const {
    validatedItems: validatedRoles,
    handleValidateItem: handleValidateRoleBase,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'user-roles',
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'user-roles',
  });

  // Wrapper function to handle version-specific validation
  const handleValidateRole = async (roleId) => {
    // Create version-specific ID
    const versionSpecificId = `${roleId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateRoleBase(versionSpecificId);
  };

  // Create wrapper functions to maintain compatibility with existing code
  const handleStartEditRole = (roleId, role) => {
    // Extract content for editing, prioritizing rawContent
    // Don't strip any content - just use the full rawContent or content
    // The title is already handled separately in the accordion header
    let contentToEdit = '';
    if (role.rawContent) {
      // Only remove the role title header if present at the very start
      // Pattern: "# 1. **Title**" or "## 1. Title" at the beginning only
      contentToEdit = role.rawContent
        .replace(/^#+\s*\d+\.\s*\*\*[^*]+\*\*\s*\n+/, '') // # 2. **Title**
        .replace(/^#+\s*\d+\.\s*[^\n]+\n+/, ''); // # 2. Title
    } else if (role.content) {
      // Use content as-is - it should already be without the title header
      contentToEdit = role.content;
    }
    // Pass the title for editing as well
    handleStartEdit(roleId, contentToEdit, role.title || '');
  };

  const handleCopyRole = (role) => {
    return handleCopyRoleOriginal(role);
  };

  const handleExplainRole = (role) => {
    requestExplanation(role);
  };

  const handleImproveRole = (role) => {
    // Ensure the role has all necessary properties for improvement
    const roleWithType = {
      ...role,
      type: role.type || 'user-role',
      rawContent: role.rawContent || role.content,
      content: role.content
    };
    handleImproveClick(roleWithType);
  };

  // Handler for adding new role
  const handleAddNewRole = async (newSectionData) => {
    try {
      // Generate role format for display
      const roleNumber = parsedRoles.length + 1;
      const formattedRoleId = `role-${roleNumber}`;

      // Ensure content has proper structure
      let formattedContent = newSectionData.content;

      // Create the role object
      const displayRole = {
        id: formattedRoleId,
        title: `${roleNumber}. ${newSectionData.title}`,
        content: formattedContent,
        rawContent: formattedContent,
        type: 'user-role'
      };

      // Only add to the latest version
      if (!isLatestVersion) {
        showNotification({
          message: 'Please switch to the latest version to add new roles',
          type: 'warning'
        });
        setShowAddRoleModal(false);
        return;
      }

      // Add the new role to the current roles
      const updatedRoles = [...parsedRoles, displayRole];

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedRoles(updatedRoles);

      // Expand the new role by default
      setExpandedRoles(prev => ({ ...prev, [displayRole.id]: true }));

      // Update conversation messages with new role
      const roleText = `### ${displayRole.title}\n\n${displayRole.content}`;
      const updatedContent = rolesContentRef.current + '\n\n' + roleText;

      // Update content ref
      rolesContentRef.current = updatedContent;

      // Update the current version with new roles using the hook's update function
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          roles: updatedRoles,
          content: updatedContent.trim(),
          parsedContent: updatedRoles
        });
      }

      // Save to backend
      const updatedMessages = [...conversation.messages];
      let messageUpdated = false;

      // Try to match by messageIndex first (most reliable for V1)
      if (currentVersion && currentVersion.messageIndex !== undefined) {
        const idx = currentVersion.messageIndex;
        if (updatedMessages[idx] && updatedMessages[idx].role === 'assistant') {
          updatedMessages[idx] = {
            ...updatedMessages[idx],
            content: updatedContent.trim()
          };
          messageUpdated = true;
          console.log('✅ UserRoles Add: Updated message by messageIndex at index', idx);
        }
      }

      // Fallback: update the last assistant message if we couldn't find by messageIndex
      if (!messageUpdated) {
        console.warn('⚠️ UserRoles Add: No messageIndex match, updating last assistant message');
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      showNotification({
        message: 'Role added successfully',
        type: 'success'
      });

      // Close modal after successful save
      setShowAddRoleModal(false);

      // Update LHS immediately by calling onConversationUpdate with updated messages
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id, updatedMessages);
        console.log('✅ UserRoles Add: Called onConversationUpdate');
      }

      // Scroll to the new role
      setTimeout(() => {
        if (rolesPanelRef.current) {
          rolesPanelRef.current.scrollTop = rolesPanelRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      showNotification({
        message: 'Failed to add new role',
        type: 'error'
      });
    }
  };

  // Handle update from improvement modal
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;

    try {
      // Handle remove action — delete section from artifact
      if (improveSection.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        // Trigger a conversation refresh to pick up the change
        if (typeof onConversationUpdate === 'function') onConversationUpdate();
        return;
      }


      // Use merged content if it's a specific section improvement
      const contentToUse = improveSection.mergedContent || improvedContent;

      // Update the role with improved content
      const updatedRoles = [...parsedRoles].map(r => {
        if (r.id === improveSection.id) {
          // Remove duplicate title from improved content
          let cleanedContent = contentToUse;

          // Remove title if it appears at the beginning of improved content
          const titleText = r.title.replace(/^\d+\.\s*/, ''); // Remove number prefix
          const roleNumber = r.title.match(/^(\d+)\./)?.[1] || '';

          // Extended patterns to handle various formats
          const patterns = [
            // Header with number and bold title: # 7. **Privacy Officer**
            new RegExp(`^#\\s*${roleNumber}\\.\\s*\\*\\*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*\\s*\\n*`, 'i'),
            // Header with number and title: # 7. Privacy Officer
            new RegExp(`^#\\s*${roleNumber}\\.\\s*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\n*`, 'i'),
            // Multiple headers with variations
            new RegExp(`^#+\\s*${roleNumber}\\.\\s*\\*\\*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*\\s*\\n*`, 'i'),
            new RegExp(`^#+\\s*${roleNumber}\\.\\s*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\n*`, 'i'),
            // Bold with number: **7. Privacy Officer**
            new RegExp(`^\\*\\*${roleNumber}\\.\\s*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*\\s*\\n*`, 'i'),
            // Plain number format: 7. Privacy Officer
            new RegExp(`^${roleNumber}\\.\\s*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\n*`, 'i'),
            // Without number variations
            new RegExp(`^#+\\s*\\*\\*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*\\s*\\n*`, 'i'),
            new RegExp(`^#+\\s*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\n*`, 'i'),
            new RegExp(`^\\*\\*${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*\\s*\\n*`, 'i'),
            new RegExp(`^${titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\n*`, 'i')
          ];

          // Try each pattern
          for (const pattern of patterns) {
            const beforeLength = cleanedContent.length;
            cleanedContent = cleanedContent.replace(pattern, '').trim();
            if (cleanedContent.length !== beforeLength) {
              break;
            }
          }

          return {
            ...r,
            content: cleanedContent
          };
        }
        return r;
      });

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedRoles(updatedRoles);

      // Reconstruct the full content with updated role
      let updatedContent = '';
      updatedRoles.forEach((role, index) => {
        if (index > 0) updatedContent += '\n\n';
        updatedContent += `### ${role.title}\n\n${role.content}`;
      });

      rolesContentRef.current = updatedContent.trim();

      // Update the current version with new roles using the hook's update function
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          roles: updatedRoles,
          content: updatedContent.trim(),
          parsedContent: updatedRoles
        });
      }

      // Save to backend
      const updatedMessages = [...conversation.messages];
      let messageUpdated = false;

      // Try to match by messageIndex first (most reliable for V1)
      if (currentVersion && currentVersion.messageIndex !== undefined) {
        const idx = currentVersion.messageIndex;
        if (updatedMessages[idx] && updatedMessages[idx].role === 'assistant') {
          updatedMessages[idx] = {
            ...updatedMessages[idx],
            content: updatedContent.trim()
          };
          messageUpdated = true;
          console.log('✅ UserRoles Improve: Updated message by messageIndex at index', idx);
        }
      }

      // Fallback: update the last assistant message if we couldn't find by messageIndex
      if (!messageUpdated) {
        console.warn('⚠️ UserRoles Improve: No messageIndex match, updating last assistant message');
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Update LHS immediately by calling onConversationUpdate with updated messages
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id, updatedMessages);
        console.log('✅ UserRoles Improve: Called onConversationUpdate');
      }

      closeImprovementModal();

      showNotification({
        message: 'Role improved successfully',
        type: 'success'
      });
    } catch (error) {
      showNotification({
        message: 'Failed to update improved content',
        type: 'error'
      });
    }
  };


  // Auto-scroll panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && rolesPanelRef.current && isUserRolesConversation) {
      setTimeout(() => {
        if (rolesPanelRef.current) {
          rolesPanelRef.current.scrollTop = rolesPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming, isUserRolesConversation]);

  // Don't show the split view if this isn't a user roles conversation
  // or if no roles are found and we're not streaming
  if (!isUserRolesConversation || (!showRolePanel && !isStreaming)) {
    return <>{children}</>;
  }

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      mt: 5
    }}>
      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(sidebarCollapsed),
          p: 2,
        }}>
          {children}
        </Box>

        {/* User roles accordion panel */}
        <Box
          ref={rolesPanelRef}
          sx={{
            ...getRightPanelStyles(sidebarCollapsed),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            {/* Fixed header section */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <SectionHeader label='User roles' />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {hasVersions && versions.length > 1 && (
                  <VersionSelector
                    versions={versions}
                    currentVersionIndex={currentVersionIndex}
                    onVersionChange={(newIndex) => {
                      // Reset local update flag when user explicitly switches versions
                      isLocalUpdateRef.current = false;
                      console.log('🔄 UserRoles: Version changed - reset isLocalUpdateRef');
                      setCurrentVersionIndex(newIndex);
                    }}
                    disabled={isStreaming}
                  />
                )}
                <TopBarButtons
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  onAdd={isLatestVersion ? () => setShowAddRoleModal(true) : null}
                  getExportContent={getExportContent}
                  onExport={handleExportRoles}
                  isExporting={isExporting}
                  onCopy={handleCopyRoles}
                  onRefresh={handleRefreshRoles}
                  onSave={projectId ? () => handleSaveToDocument('USER_ROLES') : undefined}
                  isSaving={isSaving || isSavingDocument}
                  disabled={!rolesContentRef.current}
                  addTooltip={!isLatestVersion ? "Switch to latest version to add roles" : "Add new role"}
                  exportTooltip="Export user roles as markdown"
                  copyTooltip="Copy user roles to clipboard"
                  refreshTooltip="Refresh user roles"
                  saveTooltip="Save as document"
                />
              </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'user-roles'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedRoles}
              totalItems={parsedRoles?.length || 0}
                currentUserMemberType={currentUserMemberType}
            />
            {/* Scrollable content section */}
            <Box sx={{
              p: 3,
              pt: 2,
              flex: 1,
              overflow: "auto",
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: 'transparent',
              },
            }}>

              {/* Show user roles content or a placeholder if none found yet */}
              {isStreaming ? (
                <LoadingBox message='Generating new user roles...' spinnerSize={40} />
              ) : showRolePanel && parsedRoles.length > 0 ? (
                <Box>
                  {/* Render roles as accordions */}
                  {parsedRoles.slice(0, visibleRoles).map((role, index) => {
                    // Use version-specific ID for validation check
                    const versionSpecificId = `${role.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;

                    return (
                      <Accordion
                        key={role.id}
                        ref={(el) => { registerSectionRef(role.id, el); if (shouldHighlight(role.id)) highlightRef.current = el; }}
                        title={role.title}
                        expanded={expandedRoles[role.id] || false}
                        onChange={() => handleRoleToggle(role.id)}
                        titleContent={editingRole === role.id ? (
                          <Box title="" onClick={(e) => e.stopPropagation()} sx={{ maxWidth: '93%', width: '93%' }}>
                            <TextField
                              value={editedTitle}
                              onChange={(e) => setEditedTitle(e.target.value)}
                              onKeyDown={(e) => e.stopPropagation()}
                              onFocus={(e) => e.target.removeAttribute('title')}
                              size="small"
                              fullWidth
                              placeholder="Enter role title"
                              variant="outlined"
                              InputProps={{ title: '' }}
                              inputProps={{ title: '', autoComplete: 'off' }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  fontSize: '14px',
                                  fontFamily: 'inherit',
                                  backgroundColor: '#fff',
                                  '& fieldset': {
                                    borderWidth: '1px',
                                  },
                                  '&:hover fieldset': {
                                    borderWidth: '1px',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderWidth: '1px',
                                  },
                                },
                                '& .MuiOutlinedInput-input': {
                                  fontSize: '14px',
                                  fontFamily: 'inherit',
                                  padding: '8px 12px',
                                },
                              }}
                            />
                          </Box>
                        ) : null}
                        rightIcon={
                          <Box
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await handleValidateRole(role.id);
                              } catch (error) {
                                // Error handled by useValidation hook
                              }
                            }}
                            sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}
                          >
                            {validatedRoles[versionSpecificId] ? (
                              <ValidatedIcon size={25} memberType={getValidatorType(versionSpecificId)} />
                            ) : (
                              <UnvalidatedIcon size={25} />
                            )}
                          </Box>
                        }
                      >
                          <ScrollableBox
                            sx={{
                              textAlign: 'justify',
                              cursor: editingRole === role.id ? 'default' : 'pointer',
                            }}
                          >
                            {/* Show edit mode or content */}
                            {editingRole === role.id ? (
                              // Edit mode
                              <EditableTextField
                                value={editedContent}
                                onChange={(e) => setEditedContent(e.target.value)}
                                minRows={10}
                              />
                            ) : (
                              // Normal content display - using markdown renderer
                              <Box sx={{
                                width: '100%',
                                '& .MuiPaper-root': {
                                  padding: '0 !important',
                                  paddingBottom: '0 !important'
                                },
                                '& p:last-child': {
                                  marginBottom: 0
                                },
                                '& ul:last-child': {
                                  marginBottom: 0,
                                  paddingBottom: 0
                                }
                              }} ref={getSectionContentRef(role.id)}>
                                <SectionDetailsRenderer section={role} taskId="user-roles" projectId={projectId} />
                                <TextSelectionPopup
                                  containerRef={getSectionContentRef(role.id)}
                                  sectionId={role.id}
                                  onSubmitComment={addComment}
                                  disabled={editingRole === role.id}
                                />

                                {/* Inline improve input */}
                                {showImproveInput === role.id && (
                                  <InlineImproveInput
                                    improvePrompt={improvePrompt}
                                    onPromptChange={setImprovePrompt}
                                    onSend={() => handleSendImprove(role)}
                                    onCancel={handleCancelImprove}
                                    promptText="How would you like to improve this role?"
                                    placeholder="Describe the improvements you'd like..."
                                  />
                                )}
                              </Box>
                            )}
                          </ScrollableBox>
                          {/* Action Buttons */}
                          <Box sx={{
                            position: 'absolute',
                            right: 10,
                            top: 10,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5
                          }}>
                            <ActionButtons
                              item={role}
                              itemIndex={index}
                              featureId={role.id}
                              editingFeature={editingRole}
                              isSaving={isSaving}
                              onEdit={() => handleStartEditRole(role.id, role)}
                              onSave={() => handleSaveEdit(role.id)}
                              onCancel={handleCancelEdit}
                              onImprove={() => handleImproveRole(role)}
                              onExplain={() => handleExplainRole(role)}
                              onCopy={() => handleCopyRole(role)}
                              onDelete={() => handleDeleteRoleClick(role)}
                              vertical={true}
                          onComment={() => openCommentPanel(role.id)}
                          commentCount={commentCounts[role.id]?.open || 0}
                          isApproved={validatedRoles[versionSpecificId]}
                          approvedByType={validatedRoles[versionSpecificId] ? getValidatorType(versionSpecificId) : null}
                            />
                          </Box>
                      </Accordion>
                    );
                  })}

                  {/* Load more button for lazy loading */}
                  {parsedRoles.length > visibleRoles && (
                    <LoadMoreButton
                      remaining={parsedRoles.length - visibleRoles}
                      onLoadMore={handleLoadMore}
                      onShowAll={handleShowAll}
                      label="Load more"
                    />
                  )}
                </Box>
              ) : (
                <EmptyContentPlaceholder
                  title="No user roles found yet."
                  description="Ask the AI to define the user roles for your application. You can use prompts like:"
                  examplePrompt="Can you help me define the main user roles for my application including their responsibilities and permissions?"
                  additionalInfo="You can also ask for specific role definitions, access levels, workflows, or relationships between different user types."
                />
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete User Role"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height='auto'
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainSection?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        onUpdate={handleUpdateImprovedContent}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />

      {/* Add Section Modal */}
      <AddSectionModal
        open={showAddRoleModal}
        onClose={() => setShowAddRoleModal(false)}
        onSave={handleAddNewRole}
        sectionType="userRoles"
        existingSections={parsedRoles}
      />
    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (parsedRoles || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewUserRoles;